import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';
import { useAuth } from '@techsofit/app-kit';
import { cn } from '@techsofit/ui';
import { useGymData } from '../data/GymDataProvider';
import { subscriptionAlert } from '../lib/subscription';

/**
 * Surfaces the gym's own subscription state — lapsed, or about to lapse — wherever
 * it's dropped (the dashboard, mainly), so the owner doesn't have to open Billing
 * to find out. Only owners get the renew CTA; staff get a heads-up.
 */
export function SubscriptionBanner({ className }: { className?: string }) {
  const { subscription } = useGymData();
  const { user } = useAuth();
  const alert = subscriptionAlert(subscription);
  if (!alert) return null;

  const isOwner = user?.role === 'GYM_OWNER';
  const expired = alert.tone === 'expired';
  const Icon = expired ? AlertTriangle : Clock;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card px-4 py-3',
        expired ? 'bg-bad-soft text-bad-ink' : 'bg-dark text-on-dark',
        className,
      )}
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{alert.title}</p>
        <p className={cn('text-sm', expired ? 'text-bad-ink/80' : 'text-on-dark-muted')}>{alert.body}</p>
      </div>
      {isOwner ? (
        <Link
          to="/billing"
          className={cn(
            'shrink-0 rounded-control px-3.5 py-2 text-sm font-medium transition-opacity hover:opacity-90',
            expired ? 'bg-bad text-on-dark' : 'bg-accent text-accent-ink',
          )}
        >
          {expired ? 'Renew now' : 'View plans'}
        </Link>
      ) : (
        <p className={cn('shrink-0 text-sm font-medium', expired ? 'text-bad-ink' : 'text-on-dark-muted')}>
          Ask the owner to renew.
        </p>
      )}
    </div>
  );
}
